import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@invent/wl-ui-kit-next/ui';
import { KeyRound } from 'lucide-react';
import * as React from 'react';

import {
  ADDRESS_HINTS,
  COMMON_PASSWORDS,
  MIGRATED_ACCOUNTS,
  PASSWORD_MIN_LENGTH,
  SCENARIOS,
  UNENTITLED_ACCOUNT,
  journey,
  type IScenario,
  type JourneyCode,
  type ProviderMode
} from './fixtures';

/**
 * Every value that reaches a branch, in one panel.
 *
 * It exists because the invented registry holds seven numbers and everything else is a
 * `not_found` by construction — so a reviewer without the list spends their first attempt on
 * the wrong branch and their second wondering whether the prototype is broken. The list used
 * to live in the notes under the anchor screen, which put it behind the chrome switch, which
 * put it out of reach in exactly the mode most people open the page in.
 *
 * A sheet rather than a note on the screen: it is a crib for whoever is driving, not part of
 * what is being reviewed, and it has to be available on every screen because the values it
 * lists are spread across three of them.
 */

const REGISTERING: JourneyCode[] = ['fa', 'teams', 'ria', 'fsp'];

/**
 * The switch position a row needs, when it is not the one currently set.
 *
 * Both halves are said out loud rather than one of them being inferred from the other: a
 * row needing the registries live and a row needing them stubbed are two different
 * scenarios, and the one that needs them stubbed is the branch the product takes today.
 */
function switchPositionNeeded(
  scenario: IScenario,
  providerMode: ProviderMode
): string | undefined {
  if (scenario.needsLiveProvider && providerMode === 'stubbed') return 'needs registries live';
  if (scenario.needsStubbedProvider && providerMode === 'live') return 'needs registries stubbed';
  return undefined;
}

export function TestDataSheet({
  providerMode,
  className
}: {
  providerMode: ProviderMode;
  className?: string;
}) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          <KeyRound className="size-3" aria-hidden /> Test data
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>What to type</SheetTitle>
          <SheetDescription>
            Invented, all of it but the firms a team is routed by, which are survey-api’s own
            register. Ten CRDs, two NPN-and-licence pairs and two SEC numbers — anything else
            answers <span className="font-mono">not_found</span>, because the registry here is a
            table rather than a service. A team with no CRD has a button of its own.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-6 px-4 pb-8">
          {providerMode === 'stubbed' && (
            <p className="rounded-md border border-dashed px-3 py-2 text-xs text-[var(--color-deep-magenta)]">
              NIPR is switched to <strong>stubbed</strong>, which is what the product does
              today: every FSP lookup answers <span className="font-mono">error</span> whatever
              you type, and the rows marked below are unreachable until you switch it to live.
              What still branches there is ours rather than a registry’s — a number our own
              data holds. BrokerCheck and SEC ADV are real in the product, so FA, RIA and team
              numbers answer alike in both positions.
            </p>
          )}

          <Section title="Sign in · a migrated advisor">
            <Row
              value={MIGRATED_ACCOUNTS.returning.email}
              note={
                `Password: ${MIGRATED_ACCOUNTS.returning.password}. One of the 9 716 whose ` +
                'legacy password survived the policy — signs in, then has to change it.'
              }
            />
            <Row
              value={UNENTITLED_ACCOUNT.email}
              note={
                `Password: ${UNENTITLED_ACCOUNT.password}. One of the 10 586 the scoped ` +
                'catalogue answers [] for — same two screens, nothing behind them.'
              }
            />
          </Section>

          {REGISTERING.map((code) => {
            const rows = SCENARIOS.filter((scenario) => scenario.journey === code);
            if (rows.length === 0) return null;

            return (
              <Section key={code} title={journey(code).label}>
                {rows.map((row) => (
                  <Row
                    key={`${row.label}-${row.value}`}
                    value={row.value}
                    label={row.label}
                    note={row.outcome}
                    unreachable={switchPositionNeeded(row, providerMode)}
                  />
                ))}
              </Section>
            );
          })}

          <Section title="Addresses, at the last step">
            {ADDRESS_HINTS.map((hint) => (
              <Row key={hint.email} value={hint.email} note={hint.outcome} />
            ))}
            <p className="text-xs text-muted-foreground">
              Any other address is free and completes the registration.
            </p>
          </Section>

          <Section title="Passwords">
            <p className="text-xs text-muted-foreground">
              The policy is the server’s: at least {PASSWORD_MIN_LENGTH} characters, not on the
              blocklist, and it must not contain the address’s own name. So{' '}
              <span className="font-mono">short</span> is refused for length,{' '}
              <span className="font-mono">{COMMON_PASSWORDS[0]}</span> for being common, and{' '}
              <span className="font-mono">miriam-and-more</span> for an address starting{' '}
              <span className="font-mono">miriam@</span>. Anything long and unremarkable
              passes.
            </p>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Row({
  value,
  label,
  note,
  unreachable
}: {
  value: string;
  label?: string;
  note: string;
  /** The switch position this row needs, when it is not the one currently set. */
  unreachable?: string;
}) {
  return (
    <div className="grid gap-0.5 border-b border-dashed pb-2 last:border-b-0">
      <p className="flex flex-wrap items-center gap-2">
        {/* Selectable rather than a copy button: navigator.clipboard needs a secure context
            and can be refused, and a crib that silently copies nothing is worse than one you
            read. */}
        <span className="font-mono text-sm text-[var(--color-royal-blue)] select-all">
          {value}
        </span>
        {unreachable && (
          <Badge variant="outline" className="border-dashed text-[10px]">
            {unreachable}
          </Badge>
        )}
      </p>
      {label && <p className="text-xs font-medium">{label}</p>}
      <p className="text-xs text-muted-foreground">{note}</p>
    </div>
  );
}
